import { createContext, useContext } from "react";
import type { TenantSlug } from "../query-keys/qk";

/**
 * Tenant-slug React context. `@linkhr/auth` (Phase 1) populates this via a
 * `<TenantProvider>` that reads `window.location.hostname.split(".")[0]` (with
 * a `VITE_DEV_TENANT_SLUG` fallback for localhost dev).
 *
 * Until Phase 1 lands, apps can inject a slug manually:
 *
 *   <TenantSlugContext.Provider value={asTenantSlug("acme")}>
 *     <ApiProvider ...>{children}</ApiProvider>
 *   </TenantSlugContext.Provider>
 */
export const TenantSlugContext = createContext<TenantSlug | null>(null);

/**
 * Reads the current tenant slug from context. Throws if no provider is present
 * — this is a programmer error (the app shell must resolve the tenant before
 * rendering any screen that calls this hook).
 */
export function useTenantSlug(): TenantSlug {
  const slug = useContext(TenantSlugContext);
  if (!slug) {
    throw new Error(
      "useTenantSlug() requires <TenantSlugContext.Provider value={...}> ancestor (populated by @linkhr/auth in Phase 1).",
    );
  }
  return slug;
}
